'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';

import { EmailVehicleFormValues, emailVehicleSchema } from '@/slw/entities/listing/listing.schema';
import { Button } from '@/slw/shared/ui/button';
import { Dialog, DialogContent } from '@/slw/shared/ui/dialog';
import FormProvider, { Checkbox, Input, Textarea } from '@/slw/shared/ui/form';
import { useAuthStore } from '@/slw/shared/stores';

export function EmailVehicleModal({
  open,
  onOpenChange,
  onSendEmail,
  isLoading,
}: {
  open: boolean;
  onSendEmail?: (values: EmailVehicleFormValues) => void;
  onOpenChange: (v: boolean) => void;
  isLoading?: boolean;
}) {
  const { user } = useAuthStore();

  const form = useForm<EmailVehicleFormValues>({
    resolver: zodResolver(emailVehicleSchema),
    defaultValues: {
      yourName: `${user?.firstName} ${user?.lastName}` || '',
      yourEmail: user?.email || '',
      friendName: '',
      friendEmail: '',
      message: '',
      sendMeACopy: false,
      signMeUp: false,
    },
  });

  useEffect(() => {
    form.reset({
      yourName: `${user?.firstName} ${user?.lastName}` || '',
      yourEmail: user?.email || '',
      friendName: '',
      friendEmail: '',
      message: '',
      sendMeACopy: false,
      signMeUp: false,
    });
  }, [form, open, user?.email, user?.firstName, user?.lastName]);

  const onSubmit = (data: EmailVehicleFormValues) => {
    onSendEmail?.(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='md:max-w-[668px] gap-4 max-h-screen md:max-h-[900px]'>
        <h2 className='text-2xl font-bold mt-3'>Email vehicle to a friend</h2>
        <div className='overflow-y-auto max-h-screen p-0'>
          <div className='max-h-[calc(100vh-200px)]'>
            <FormProvider {...form} onSubmit={onSubmit}>
              <div className='flex flex-col gap-4'>
                <Input name='yourName' label='Your name' required placeholder='Enter your name' direction='column' />
                <Input
                  name='yourEmail'
                  label='Your email'
                  required
                  placeholder='Your email address'
                  direction='column'
                  type='email'
                />
                <Input
                  name='friendName'
                  label="Friend's name"
                  required
                  placeholder='Enter your name'
                  direction='column'
                />
                <Input
                  name='friendEmail'
                  label="Friend's email"
                  required
                  placeholder='Your email address'
                  direction='column'
                  type='email'
                />
                <Textarea
                  name='message'
                  label='Your message'
                  required
                  placeholder='Your message'
                  direction='column'
                  rows={4}
                />
                <div className='flex items-center gap-2'>
                  <Checkbox color='green' name='sendMeACopy' direction='row' />
                  <label htmlFor='sendMeACopy' className='text-sm'>
                    Send me a copy of this message
                  </label>
                </div>
              </div>
              <Button type='submit' color='primary' variant='default' className='w-full mt-4' disabled={isLoading}>
                {isLoading ? 'Sending...' : 'SEND MESSAGE'}
              </Button>
            </FormProvider>
            <p className='text-sm text-[#979696]'>
              Per our{' '}
              <Link href='/privacy-policy' className='text-[#B6864C] underline'>
                Privacy Policy
              </Link>
              , the information provided will only be used to ensure delivery.
              <br />
              Using this form to send spam is a violation of our{' '}
              <Link href='/terms-and-conditions' className='text-[#B6864C] underline'>
                Terms and Conditions
              </Link>
              .
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
